/* eslint react/no-this-in-sfc: "off", prefer-destructuring: "off", no-unused-vars: "off" */
import * as _ from 'lodash';
import { log } from 'limuirs-utils';

export default class SubscriptionStore {
  constructor(restManager, prefetch, boardLabelFn = () => {}, catLabelFn = () => {}) {
    // Prefetch data
    this.prefetch = prefetch;
    this.storeHasInit = false;
    this.subscribedNodeIds = [];
    this.nodes = [];
    this.topCategory = prefetch.getData('topCategory')[0];
    this.allowed = _.get(
      prefetch.getData('policyCheck'),
      'policy_check.checks[0].allowed',
      _.get(prefetch.getData('policyCheck'), 'policy_check.checks.items[0].allowed', false)
    );

    this.boardLabelFn = boardLabelFn;
    this.catLabelFn = catLabelFn;
    this.restManager = restManager;

    if (prefetch.isDataLoadedForQuery('nodes')) {
      this.initSubscribedNodeIds(prefetch.getData('nodes'));
    }
  }

  /**
   * Loads nodes data in the store that is lazily fetched
   */
  initStoreData() {
    return this.prefetch.waitForData('nodes').then(this.initSubscribedNodeIds.bind(this));
  }

  /**
   * @returns {boolean} whether all the data in the store has been fetched and ready to use
   */
  get storeDataReady() {
    return this.storeHasInit;
  }

  /**
   * Initialize the subscribed nodes id array
   */
  initSubscribedNodeIds(nodes) {
    if (!this.storeHasInit) {
      // Only do this once
      if (_.get(this.topCategory, 'user_context.is_subscribed', false)) {
        this.subscribedNodeIds.push(this.topCategory.id);
      }

      this.nodes = _.filter(nodes, node => _.get(node, 'user_context.can_subscribe'));

      this.nodes.forEach(node => {
        if (_.get(node, 'user_context.is_subscribed', false)) {
          this.subscribedNodeIds.push(node.id);
        }
      });
      this.storeHasInit = true;
    }
  }

  /**
   * @returns {Array} of all node ids under the current category, including the current category
   */
  get allNodeIds() {
    const ids = [this.topCategory.id];
    return ids.concat(_.map(this.nodes, node => node.id));
  }

  /**
   * @returns {{id, value, title, label, type, children}} the node tree structure
   */
  get tree() {
    return this.createTree(this.topCategory);
  }

  /**
   * Recursive function to create a tree structure based on the node hierarchy
   * of the category.
   *
   * @param {Object} cat the category
   * @returns {*} the tree structure or null when empty
   */
  createTree = cat => {
    const subNodes = _.filter(this.nodes, {
      parent: { id: SubscriptionStore.normalizedCategoryId(cat.id) }
    });
    const subCats = _.filter(subNodes, { node_type: 'category' });
    const boards = _.filter(subNodes, { node_type: 'board' });
    const mappedSubCats = _.map(subCats, this.createTree);
    const mappedBoards = _.map(boards, board => ({
      id: board.id,
      value: board.id, // Yes, we're duplicating, so that this structure can be used directly by CheckboxTree
      title: board.title,
      label: this.boardLabelFn(board),
      type: 'board'
    }));

    const children = _.compact(_.concat(mappedSubCats, mappedBoards));
    const boardCount = this.countsForCategoryChildren(children).boardTotalCount;

    if (boardCount) {
      return {
        id: cat.id,
        value: cat.id, // Yes, we're duplicating, so that this structure can be used directly by CheckboxTree
        title: cat.title,
        label: this.catLabelFn(cat, boardCount),
        type: 'category',
        children
      };
    }
    // No sub-boards, drop this category
    return null;
  };

  /**
   * @returns {boolean} whether there is a partial set of subscriptions for the category
   */
  get hasPartialSubscription() {
    // Add 1 to node length since "nodes" does not contain the top category
    return (
      this.subscribedNodeIds.length > 0 && this.nodes.length + 1 !== this.subscribedNodeIds.length
    );
  }

  /**
   * Create a tree object with all of the selected nodes from the UI to be
   * subscribed to. This is used by the REST API call for {@code subscriptions/}.
   *
   * @param {Object} node the node to create the tree for
   * @param {Array} selectedSubIds subscription ids
   * @param {Number} depth the tree depth
   * @returns {{type: string, target: {type, id}, includes?: Array, excludes?: Array}} tree object
   * formatted for REST API usage containing the selected categories and boards to be subscribed to.
   */
  filterUnsubscribedNodes = (node, selectedSubIds, depth) => {
    if (node) {
      const subbedChildren =
        node.children && node.children.length > 0
          ? _.compact(
              _.map(node.children, children =>
                this.filterUnsubscribedNodes(children, selectedSubIds, depth + 1)
              )
            )
          : [];
      // handle case where all children are de-selected
      if ((!subbedChildren || subbedChildren.length === 0) && depth === 0) {
        const excluded = node.children.map(child => this.buildSubscriptionObject(child));
        return Object.assign({ excludes: excluded }, this.buildSubscriptionObject(node));
      }
      const nodeIsSubbed = selectedSubIds.indexOf(node.id) > -1 && node.type === 'board';
      if (subbedChildren.length > 0 || nodeIsSubbed) {
        return Object.assign({ includes: subbedChildren }, this.buildSubscriptionObject(node));
      }
    }

    return null;
  };

  /**
   * Build a subscription object for REST API usage.
   * @param node The target node
   * @returns {{type: string, target: {type, id}}} tree object formatted for REST API usage containing
   * the selected category to be subscribed to.
   */
  buildSubscriptionObject = node => {
    const type = node.type;
    const id = SubscriptionStore.removeNodeTypePrefixFromId(node.id);
    return {
      type: 'subscription',
      target: {
        type,
        id
      }
    };
  };

  /**
   * Convenience method that performs the appropriate subscription
   * action based on the group of subscriptions to be subscribed.
   *
   * @param {String} type the type of subscription, either {@code partial}, {@code all}, or {@code none}
   * @param selected the set of node ids to be subscribed to
   * @returns {*} a promise for the REST response that performs the action
   */
  subscribe = ({ type, selected }) => {
    if (type === 'partial') {
      return this.subscribeSelected(selected);
    }
    if (type === 'all') {
      return this.subscribeAll();
    }
    return this.unsubscribe();
  };

  /**
   * Subscribes the user to all the nodes in the category
   *
   * @returns {*|Promise.<T>} a promise for the subscribe REST call response
   */
  subscribeAll = () => {
    const allSubbed = SubscriptionStore.flattenNode(this.tree)
      .slice(0, -1)
      .map(node => node.id);
    return this.subscribeSelected(allSubbed);
  };

  /**
   * Subscribes the user to the selected node ids
   *
   * @param {Array} selectedSubIds a list of the node ids to subscribe to
   * @returns {*|Promise.<T>} a promise for the subscribe REST call response
   */
  subscribeSelected = selectedSubIds => {
    const subs = this.filterUnsubscribedNodes(this.tree, selectedSubIds, 0);
    if (subs) {
      return this.restManager
        .post(`/categories/${this.topCategory.id}/subscriptions`, subs)
        .then(this.subscribeSelectedSuccess.bind(this))
        .catch(error => {
          log.error(`Error subscribing to ids '${selectedSubIds}'`, error);
          throw error;
        });
    }
    return Promise.resolve({ data: { items: [], size: 0 } });
  };

  /**
   * Subscribe selected success handler
   *
   * @param {Object} data the response data
   * @param {Number} httpCode the response http code
   * @returns {{data: *, httpCode: *}} the original response data and http code
   */
  subscribeSelectedSuccess({ data, httpCode }) {
    this.subscribedNodeIds = [];
    data.items.forEach(
      sub => this.subscribedNodeIds.push(SubscriptionStore.addNodeTypePrefixToId(sub)),
      this
    );
    return { data, httpCode };
  }

  /**
   * Unsubscribes user from all subscriptions for the category
   *
   * @returns {*} a promise for the delete REST call response
   */
  unsubscribe = () => {
    if (this.isTopCatSubscribed) {
      log.info('Unsubscribing to category');
      return this.restManager
        .delete(`/categories/${this.topCategory.id}/subscriptions`)
        .then(this.unsubscribeSuccess.bind(this))
        .catch(this.unsubscribeError.bind(this));
    }
    return Promise.resolve({ data: { items: [], size: 0 } });
  };

  /**
   * Success handler for unscubscribe action
   *
   * @param {Number} httpCode the HTTP response code
   * @returns {{data: {items: Array}, httpCode: *}} proxy of the response object
   */
  unsubscribeSuccess({ httpCode }) {
    this.subscribedNodeIds = [];
    return { data: { items: [], size: 0 }, httpCode };
  }

  /**
   * Error handler for unscubscribe action
   *
   * @param {Object} err the error response
   */
  unsubscribeError = err => {
    log.error(`Error unsubscribing from top category '${this.topCategory.id}'`, err);
    throw err;
  };

  /**
   * @returns {boolean} if the top category is subscribed
   */
  get isTopCatSubscribed() {
    return this.storeDataReady && this.topCategory && this.subscribedNodeIds
      ? this.subscribedNodeIds.indexOf(this.topCategory.id) !== -1
      : _.get(this.topCategory, 'user_context.is_subscribed', false);
  }

  /**
   * Counts for the top category, will return count values of 0 if the store
   * is lazily loaded and all the data has not been loaded yet.
   *
   * @returns {{boardTotalCount: Number, boardSubCount: Number}} an object containing counts
   */
  get counts() {
    const children =
      this.storeDataReady && this.topCategory ? _.get(this.tree, 'children', []) : [];
    return this.countsForCategoryChildren(children);
  }

  /**
   * @param {Array} children the child nodes for a category
   * @returns {{boardTotalCount: Number, boardSubCount: Number}} an object containing counts
   */
  countsForCategoryChildren = children => {
    const boards = _.filter(_.flatMap(children, SubscriptionStore.flattenNode), { type: 'board' });
    return {
      boardTotalCount: boards.length,
      boardSubCount: _.filter(boards, board => this.subscribedNodeIds.indexOf(board.id) > -1).length
    };
  };

  /**
   * @return {Array} the list of checked, or subscribed, node ids as used to initialize the state
   * of UI components displaying the node subscription state.
   */
  get checkedNodeIds() {
    return this.isTopCatSubscribed && this.hasPartialSubscription
      ? this.subscribedNodeIds
      : this.allNodeIds;
  }

  /**
   * Normalizes the category id to contain the type prefix
   *
   * @param {Object} id the category id
   * @returns {string} the category id with the type prefix
   */
  static normalizedCategoryId(id) {
    return id.indexOf(':') > -1 ? id : `category:${id}`;
  }

  /**
   * Removes the node type prefix and returns the id for
   * values that use the pattern {@code type:id}.
   *
   * @param {String} nodeIdWithType the node id that may contain the node type prefix
   * @returns {String} the node id
   */
  static removeNodeTypePrefixFromId(nodeIdWithType) {
    return nodeIdWithType.indexOf(':') > -1 ? nodeIdWithType.split(':')[1] : nodeIdWithType;
  }

  /**
   * Normalizes the target node id to contain the type prefix
   *
   * @param {{type: string, target: {type, id}}} sub subscription object
   * @returns {String} the target node id that is stored into the array {@code subscribedNodeIds
   * and the prefix type is added to match the node id in actual tree object
   * so that subscribed nodes count can be calculated correctly.
   */
  static addNodeTypePrefixToId(sub) {
    if (sub.target.type != null) {
      return sub.target.type === 'category'
        ? `category:${sub.target.id}`
        : `board:${sub.target.id}`;
    }
    return sub.target.id;
  }

  /**
   * Flattens a node object that has the structure:
   *
   * <pre><code>
   * {
   *   ...,
   *   children: [{
   *     ...
   *     children: [{...}]
   *   }]
   * }
   * </pre></code>
   *
   * @param {Object} node the node to flatten, expects a nested property {@code children}
   * @returns {Array} the flattened nodes
   */
  static flattenNode(node) {
    if (node) {
      const flat =
        node.children && node.children.length > 0
          ? _.flatMap(node.children, SubscriptionStore.flattenNode)
          : [];
      flat.push(node);
      return flat;
    }
    return [];
  }
}
